import React from "react";

import { useTranslation } from "react-i18next";

import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import TeamIcon from "@components/icons/TeamIcon";
import TeamLineupsStore from "@state/stores/offer/statistics/TeamLineupsStore";
import { observer } from "mobx-react";

import TeamLineupsMissingPlayers from "@features/offer/shared/statistics/components/team-lineups/TeamLineupsMissingPlayers";
import TeamLineupsSubstitutions from "@features/offer/shared/statistics/components/team-lineups/TeamLineupsSubstitutions";

export default observer(function TeamLineupsPageBench(props: {
	store: TeamLineupsStore;
}) {
	const { t } = useTranslation();

	const homeSubstitutionsPlayers = (
		props.store.teamLineups?.homeTeamPlayers || []
	).slice(11);

	const awaySubstitutionsPlayers = (
		props.store.teamLineups?.awayTeamPlayers || []
	).slice(11);

	const homeMissingPlayers = props.store.teamLineups?.homeMissingPlayer || [];
	const awayMissingPlayers = props.store.teamLineups?.awayMissingPlayer || [];

	const noHomeMissingPlayers =
		!homeMissingPlayers || homeMissingPlayers.length === 0;
	const noAwayMissingPlayers =
		!awayMissingPlayers || awayMissingPlayers.length === 0;

	return (
		<ErrorBoundaryWithPropagation componentName="Desktop/TeamLineupsDataBench.tsx">
			{(homeSubstitutionsPlayers.length > 0 ||
				awaySubstitutionsPlayers.length > 0) &&
				(props.store.isLiveOrHistory ||
					props.store.teamLineups?.lineupConfirmed) && (
					<div className="statistics__card u-mar--top--4">
						<div className="statistics__header">
							<div className="statistics__title">
								{t("EVENT.STATISTICS.LINEUPS.SUBSTITUTIONS")}
							</div>
						</div>
						<div className="statistics__body">
							<table className="table table--fixed table--framed table--framed--light table--nested">
								<tbody>
									<tr>
										<td className="u-align--v--top u-pad--reset">
											<table className="table table--borderless table--fixed">
												<thead className="table__head">
													<tr className="table__head__row table__row">
														<th className="table__head__cell table__cell u-type--center">
															<TeamIcon
																showCountryFlag={
																	props.store
																		.playerStatisticsStore
																		.eventStore
																		.tournament?.showCountryFlag
																}
																flagCode={
																	props.store
																		.playerStatisticsStore
																		.eventStore
																		.teamOne?.countryCode
																}
																jerseyTypeAbrv={
																	props.store
																		.playerStatisticsStore
																		.eventStore
																		.teamOne?.jerseyTypeAbrv
																}
																opponentFlagCode={props.store.playerStatisticsStore.eventStore.teamTwo?.countryCode}
																opponentJerseyTypeAbrv={props.store.playerStatisticsStore.eventStore.teamTwo?.jerseyTypeAbrv}
																className="u-icon u-icon--base u-mar--right--2"
															/>
															{
																props.store
																	.playerStatisticsStore
																	.eventStore
																	.teamOne
																	?.name
															}
														</th>
													</tr>
												</thead>
												<tbody className="table__body">
													{homeSubstitutionsPlayers.map(
														(sp, idx) => (
															<TeamLineupsSubstitutions
																substitutionPlayers={
																	sp
																}
																key={idx}
																store={
																	props.store
																}
															/>
														)
													)}
												</tbody>
											</table>
										</td>

										<td className="u-align--v--top u-pad--reset">
											<table className="table table--borderless table--fixed">
												<thead className="table__head">
													<tr className="table__head__row table__row">
														<th className="table__head__cell table__cell u-type--center">
															<TeamIcon
																showCountryFlag={
																	props.store
																		.playerStatisticsStore
																		.eventStore
																		.tournament?.showCountryFlag
																}
																flagCode={
																	props.store
																		.playerStatisticsStore
																		.eventStore
																		.teamTwo?.countryCode
																}
																jerseyTypeAbrv={
																	props.store
																		.playerStatisticsStore
																		.eventStore
																		.teamTwo?.jerseyTypeAbrv
																}
																opponentFlagCode={props.store.playerStatisticsStore.eventStore.teamOne?.countryCode}
																opponentJerseyTypeAbrv={props.store.playerStatisticsStore.eventStore.teamOne?.jerseyTypeAbrv}
																className="u-icon u-icon--base u-mar--right--2"
															/>
															{
																props.store
																	.playerStatisticsStore
																	.eventStore
																	.teamTwo
																	?.name
															}
														</th>
													</tr>
												</thead>
												<tbody className="table__body">
													{awaySubstitutionsPlayers.map(
														(sp, idx) => (
															<TeamLineupsSubstitutions
																substitutionPlayers={
																	sp
																}
																key={idx}
																store={
																	props.store
																}
															/>
														)
													)}
												</tbody>
											</table>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				)}

			<div className="statistics__card u-mar--top--4">
				<div className="statistics__header">
					<div className="statistics__title">
						{t("EVENT.STATISTICS.LINEUPS.INJURIES_SUSPENSIONS")}
					</div>
				</div>
				<div className="statistics__body">
					<table className="table table--fixed table--framed table--framed--light table--nested">
						<tbody>
							<tr>
								<td className="u-align--v--top u-pad--reset">
									<table className="table table--borderless table--fixed">
										<thead className="table__head">
											<tr className="table__head__row table__row">
												<th className="table__head__cell table__cell u-type--center">
													<TeamIcon
														showCountryFlag={
															props.store
																.playerStatisticsStore
																.eventStore
																.tournament?.showCountryFlag
														}
														flagCode={
															props.store
																.playerStatisticsStore
																.eventStore
																.teamOne?.countryCode
														}
														jerseyTypeAbrv={
															props.store
																.playerStatisticsStore
																.eventStore
																.teamOne?.jerseyTypeAbrv
														}
														opponentFlagCode={props.store.playerStatisticsStore.eventStore.teamTwo?.countryCode}
														opponentJerseyTypeAbrv={props.store.playerStatisticsStore.eventStore.teamTwo?.jerseyTypeAbrv}
														className="u-icon u-icon--base u-mar--right--2"
													/>
													{
														props.store
															.playerStatisticsStore
															.eventStore.teamOne
															?.name
													}
												</th>
											</tr>
										</thead>
										<tbody className="table__body">
											{noHomeMissingPlayers ? (
												<tr className="table__body__tr table__row">
													<td className="table__body__cell table__cell table__cell--center">
														<span
															dangerouslySetInnerHTML={{
																__html: t<string>(
																	"EVENT.STATISTICS.LINEUPS.NO_MISSING_PLAYERS",
																	{
																		team: props
																			.store
																			.playerStatisticsStore
																			.eventStore
																			.teamOne
																			?.name,
																		interpolation:
																			{
																				escapeValue:
																					false,
																			},
																	}
																),
															}}
														/>
													</td>
												</tr>
											) : (
												homeMissingPlayers.map(
													(mp, idx) => (
														<TeamLineupsMissingPlayers
															missingPlayers={mp}
															key={idx}
														/>
													)
												)
											)}
										</tbody>
									</table>
								</td>

								<td className="u-align--v--top u-pad--reset">
									<table className="table table--borderless table--fixed">
										<thead className="table__head">
											<tr className="table__head__row table__row">
												<th className="table__head__cell table__cell u-type--center">
													<TeamIcon
														showCountryFlag={
															props.store
																.playerStatisticsStore
																.eventStore
																.tournament?.showCountryFlag
														}
														flagCode={
															props.store
																.playerStatisticsStore
																.eventStore
																.teamTwo?.countryCode
														}
														jerseyTypeAbrv={
															props.store
																.playerStatisticsStore
																.eventStore
																.teamTwo?.jerseyTypeAbrv
														}
														opponentFlagCode={props.store.playerStatisticsStore.eventStore.teamOne?.countryCode}
														opponentJerseyTypeAbrv={props.store.playerStatisticsStore.eventStore.teamOne?.jerseyTypeAbrv}
														className="u-icon u-icon--base u-mar--right--2"
													/>
													{
														props.store
															.playerStatisticsStore
															.eventStore.teamTwo
															?.name
													}
												</th>
											</tr>
										</thead>
										<tbody className="table__body">
											{noAwayMissingPlayers ? (
												<tr className="table__body__tr table__row">
													<td className="table__body__cell table__cell table__cell--center">
														<span
															dangerouslySetInnerHTML={{
																__html: t<string>(
																	"EVENT.STATISTICS.LINEUPS.NO_MISSING_PLAYERS",
																	{
																		team: props
																			.store
																			.playerStatisticsStore
																			.eventStore
																			.teamTwo
																			?.name,
																		interpolation:
																			{
																				escapeValue:
																					false,
																			},
																	}
																),
															}}
														/>
													</td>
												</tr>
											) : (
												awayMissingPlayers.map(
													(mp, idx) => (
														<TeamLineupsMissingPlayers
															missingPlayers={mp}
															key={idx}
														/>
													)
												)
											)}
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
